// Overview — the executive read on fatigue exposure across the workforce.

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { DerivedMetricKey, FatigueFilters } from '../data/types';
import { useFatigueSummary } from '../data/useFatigueData';
import { KpiCard } from '../components/KpiCard';
import { MetricBreakdownPanel } from '../components/MetricBreakdownPanel';
import { FatigueTrendChart } from '../components/charts';
import { LoadingBlock, NoteLine, RiskAlert, SectionHeading, StatRow } from '../components/primitives';

export const OverviewSection: React.FC<{ filters: FatigueFilters }> = ({ filters }) => {
  const [openMetric, setOpenMetric] = React.useState<DerivedMetricKey | null>(null);
  const { data: overview, isPending, error } = useFatigueSummary(filters);

  if (isPending) {
    return (
      <div className='flex flex-col gap-4'>
        {/* FM-13 — the grid is fluid rather than a fixed 4-up, so the cards
            reflow instead of squashing on narrower screens. */}
        <div className='grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-4'>
          {[0, 1, 2, 3].map((i) => (
            <LoadingBlock key={i} className='h-72' />
          ))}
        </div>
        <LoadingBlock className='h-80' />
      </div>
    );
  }

  if (error || !overview) {
    return (
      <Card>
        <CardContent className='p-6'>
          <p className='text-sm text-muted-foreground'>
            Fatigue figures could not be loaded. Check the client selection and try again.
          </p>
        </CardContent>
      </Card>
    );
  }

  const openMetricData = overview.metrics.find((m) => m.key === openMetric);

  // Worst team by fatigue exposure, ignoring the ones with nothing to show.
  const worst = [...overview.visibleCohorts]
    .filter((row) => !row.suppressed && !row.noData)
    .sort((a, b) => b.fedPer1000 - a.fedPer1000)[0];

  if (overview.empty) {
    return (
      <Card>
        <CardHeader>
          <SectionHeading overline='Overview' title='No usable wearable data in this window' />
        </CardHeader>
        <CardContent className='flex flex-col gap-3'>
          <p className='text-sm text-muted-foreground'>
            Nothing measurable came back for {overview.window.start} to {overview.window.end}. Either no participants in
            scope have connected a device, or none of them wore one during the window.
          </p>
          <StatRow
            label='People wearing'
            value={`${overview.coverage.activeUsers} of ${overview.coverage.enrolledUsers}`}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='flex flex-col gap-6'>
      <div className='grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-4'>
        {overview.metrics.map((metric) => (
          <KpiCard key={metric.key} metric={metric} onOpenBreakdown={() => setOpenMetric(metric.key)} />
        ))}
      </div>

      {overview.narrowed && (
        <NoteLine>
          Headline figures are recomputed across the {overview.visibleCohorts.length} team
          {overview.visibleCohorts.length === 1 ? '' : 's'} matching the site and shift filters, weighted by the days of
          data behind each one.
        </NoteLine>
      )}

      {worst && (
        <RiskAlert
          tier={worst.riskTier}
          title={`${worst.cohort.name} carries the highest fatigue exposure`}
          body={`${worst.fedPer1000} fatigue exposure days per 1,000 workdays and ${worst.lrmPct}% of shifts with load above readiness, across ${worst.cohort.headcount} people at ${worst.cohort.site}. Review the rota and rest windows for this team first.`}
        />
      )}

      <div className='grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]'>
        <Card>
          <CardHeader>
            <SectionHeading
              title='Indicator trend'
              subtitle='Last 12 weeks in fortnights, all four derived indicators.'
            />
          </CardHeader>
          <CardContent className='flex flex-col gap-3'>
            <FatigueTrendChart points={overview.trend.filter((point) => !point.empty)} />
            {overview.trend.some((point) => point.empty) && (
              <NoteLine>Fortnights with no usable data are left out of the line rather than drawn as zero.</NoteLine>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className='text-base'>Data behind these figures</CardTitle>
          </CardHeader>
          <CardContent className='flex flex-col gap-3'>
            <StatRow
              label='People wearing'
              value={`${overview.coverage.activeUsers} of ${overview.coverage.enrolledUsers}`}
            />
            <StatRow label='Coverage' value={`${overview.coverage.coveragePct}%`} />
            <StatRow label='Confidence' value={`${overview.coverage.confidenceScore}%`} />
            <StatRow label='Valid days' value={String(overview.coverage.validDataDays)} />
            <StatRow label='Missing days' value={String(overview.coverage.missingDataDays)} />
            <NoteLine>
              Figures are cohort-level, over {overview.window.start} to {overview.window.end}. Switch the data-quality
              filter above to see them using only participants with reliable wear compliance.
            </NoteLine>
          </CardContent>
        </Card>
      </div>

      <MetricBreakdownPanel metricKey={openMetric} metric={openMetricData} onClose={() => setOpenMetric(null)} />
    </div>
  );
};
